import { InputAdornment, TextField, Tooltip } from '@mui/material';
import React, { KeyboardEvent, useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import { EmailRegexp } from '../../shared/constants';
import Grid from '@mui/material/Unstable_Grid2';
import MembershipService from '../services/MembershipService';
import PasswordField from '../../shared/components/PasswordField';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import useRequestVerificationEmail from '../hooks/useRequestVerificationEmail';

interface RequestVerificationEmailProps {
  username: string;
  requestOnly: boolean;
  errorMessage: (error: string) => void;
  onEmailSent: (success: boolean, email: string) => void;
}

const RequestVerificationEmail = (props: RequestVerificationEmailProps) => {
  const { username, requestOnly } = props;
  const [noEmail, setNoEmail] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [emailIsAvailable, setEmailIsAvailable] = useState<boolean>(false);
  const [checkingEmailAvailable, setCheckingEmailAvailable] =
    useState<boolean>();
  const [showEmailStatus, setShowEmailStatus] = useState<boolean>(false);

  const tenant = AuthService.Tenant;
  const {
    requestVerificationEmail,
    loading,
    success,
    error,
    requestAddEmail,
    updating,
    updateEmail,
    updateError
  } = useRequestVerificationEmail(tenant, username);

  useEffect(() => {
    if (success) {
      if (success.requestVerificationEmail) {
        props.onEmailSent(success.requestVerificationEmail, username);
      } else {
        setNoEmail(true);
      }
    }
    if (updateEmail) {
      props.onEmailSent(updateEmail.requestAddEmail, username);
    }
  }, [success, updateEmail]);

  // Set error message when there is conflict in verifying email address.
  // PublicAPI returns status code 409 conflict when the email address is
  // already taken by another account as username or verified email.
  useEffect(() => {
    if (error && error.graphQLErrors.length) {
      if (error.graphQLErrors[0].extensions.statusCode === 409) {
        props.errorMessage('This email is already in use.');
      } else {
        props.errorMessage(error.graphQLErrors[0].message);
      }
    }
  }, [error]);

  useEffect(() => {
    if (updateError) {
      if (updateError.graphQLErrors[0]?.extensions.statusCode === 401) {
        // Assume username is correct at this stage. Because username can't be editable.
        setPasswordError('Password is wrong.');
      } else if (updateError.graphQLErrors[0]?.extensions.statusCode === 409) {
        setEmailError(
          'The requested email address is already in use. Please choose a different email.'
        );
      } else {
        props.errorMessage(updateError.graphQLErrors[0]?.message);
      }
    }
  }, [updateError]);

  const handleEmailBlur = async () => {
    if (email) {
      if (EmailRegexp.test(email)) {
        setCheckingEmailAvailable(true);
        const isEmailAvailable = await MembershipService.isUsernameAvailable(
          tenant,
          email
        );
        setEmailIsAvailable(isEmailAvailable);
        setCheckingEmailAvailable(false);
        setShowEmailStatus(true);
      } else {
        setEmailIsAvailable(undefined);
        setEmailError('Invalid email format.');
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
      handleEmailBlur();
    }
  };

  const onSend = () => {
    noEmail ? requestAddEmail(password, email) : requestVerificationEmail({});
  };

  const invalidPassword =
    !email ||
    !password ||
    !EmailRegexp.test(email) ||
    !emailIsAvailable ||
    !showEmailStatus;

  const disableSend =
    loading || updating || !username || (noEmail && invalidPassword);

  return (
    <Grid container justifyContent="center" direction="column" spacing={2}>
      <Grid>
        <Typography variant="h5" color="primary" align="center">
          Email Verification
        </Typography>
      </Grid>
      {noEmail ? (
        <>
          <Grid>
            <Typography align="center">
              There is currently no email address associated with your account.
              Enter an email address and your password below.
            </Typography>
          </Grid>
          <Grid>
            <TextField
              name="Username"
              label="Username"
              variant="outlined"
              type="text"
              fullWidth={true}
              margin="dense"
              size="small"
              value={username || ''}
              disabled
            />
          </Grid>
          <Grid>
            <TextField
              name="Email"
              label="Email"
              variant="outlined"
              type="email"
              fullWidth={true}
              margin="dense"
              size="small"
              value={email}
              required
              error={!!emailError}
              helperText={emailError}
              onFocus={() => {
                setEmailError('');
                setPasswordError('');
                setShowEmailStatus(false);
              }}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {checkingEmailAvailable ? (
                      <CustomLoadingProgress size={12} />
                    ) : showEmailStatus ? (
                      emailIsAvailable ? (
                        <CheckIcon sx={{ color: 'green' }} />
                      ) : (
                        <Tooltip
                          title="Email is in use. Please use a different email."
                          style={{ cursor: 'default' }}
                        >
                          <WarningIcon color="error" />
                        </Tooltip>
                      )
                    ) : (
                      <></>
                    )}
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid>
            <PasswordField
              label="Password"
              value={password}
              required
              error={passwordError}
              onFocus={() => {
                setPasswordError('');
                setEmailError('');
              }}
              onChange={setPassword}
            />
          </Grid>
        </>
      ) : (
        <Grid>
          <Typography align="center">
            Use this form to {requestOnly ? 're-send' : 'send'} a verification
            email to {username}.
          </Typography>
        </Grid>
      )}
      <Grid style={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          disabled={disableSend}
          onClick={onSend}
        >
          {(loading || updating) && (
            <CustomLoadingProgress size={16} style={{ paddingRight: 6 }} />
          )}
          Send
        </Button>
      </Grid>
    </Grid>
  );
};

export default RequestVerificationEmail;
